import React, { useState } from "react";
import {
  Table, Button, Space, Tag, message, Popconfirm,
  Form, Input, Drawer, Image, Upload, Switch, InputNumber, Select
} from 'antd'
import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { useApiSelector } from 'src/hooks'
import { useRequest, dispatchAsync } from 'friday-async'
import jsCookie from 'js-cookie'
import config from 'src/config'

const { TextArea } = Input

const layout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 18 },
};

// 轮播图推荐尺寸
const BANNER_SIZE = { width: 750, height: 499, label: '轮播图 750×499' }

const validateImageSize = (file: File): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new window.Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(url)
      if (img.width !== BANNER_SIZE.width || img.height !== BANNER_SIZE.height) {
        message.error(
          `图片尺寸不符：当前 ${img.width}×${img.height}，要求 ${BANNER_SIZE.label}（${BANNER_SIZE.width}×${BANNER_SIZE.height}）`,
          5
        )
        resolve(false)
      } else {
        resolve(true)
      }
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      message.error('图片读取失败')
      resolve(false)
    }
    img.src = url
  })
}

const Banner = () => {
  const apis = useApiSelector();
  const { dataArray, isValidating, revalidate } = useRequest(apis.banner.list(), {
    paginated: false,
  })

  const [drawer, setDrawer] = useState<{ visible: boolean; info: any }>({
    visible: false, info: null
  })
  const [uploading, setUploading] = useState(false)
  const [form] = Form.useForm()
  const isEdit = !!drawer.info

  // ====== CRUD ======
  const handleCreate = () => setDrawer({ visible: true, info: null })

  const handleEdit = (record: any) => {
    setDrawer({ visible: true, info: record })
  }

  const handleSubmit = async () => {
    try {
      const vals = await form.validateFields()
      let error: any
      if (isEdit) {
        const r = await dispatchAsync(apis.banner.update({ _id: drawer.info._id, ...vals }))
        error = r.error
      } else {
        const r = await dispatchAsync(apis.banner.create(vals))
        error = r.error
      }
      if (error) return message.error(isEdit ? '编辑失败' : '创建失败')
      message.success(isEdit ? '编辑成功' : '创建成功')
      setDrawer({ visible: false, info: null })
      form.resetFields()
      revalidate()
    } catch (err) { console.log(err) }
  }

  const handleRemove = async (_id: string) => {
    const { error } = await dispatchAsync(apis.banner.remove({ _id }))
    if (error) return message.error('删除失败')
    message.success('删除成功')
    revalidate()
  }

  // ====== 图片上传 ======
  const handleUpload = (info: any) => {
    if (info.file.status === 'uploading') {
      setUploading(true)
      return
    }
    if (info.file.status === 'done') {
      setUploading(false)
      const res = info.file.response
      if (res?.data?.url) {
        form.setFieldsValue({ imageUrl: res.data.url })
        message.success('图片上传成功')
      }
    } else if (info.file.status === 'error') {
      setUploading(false)
      message.error('图片上传失败')
    }
  }

  const imageUrl = Form.useWatch('imageUrl', form)

  // ====== 表格列 ======
  const columns = [
    { title: '标题', dataIndex: 'title', key: 'title', width: 180 },
    {
      title: '轮播图',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      width: 140,
      render: (v: string) => v ? (
        <Image
          src={`${config.imgHost}${v.startsWith('/') ? v.slice(1) : v}`}
          alt="轮播图"
          width={100}
          height={50}
          style={{ objectFit: 'cover', borderRadius: 4 }}
          preview={{ mask: '预览' }}
        />
      ) : <Tag>-</Tag>,
    },
    {
      title: '跳转链接',
      dataIndex: 'linkUrl',
      key: 'linkUrl',
      width: 200,
      ellipsis: true,
      render: (v: string, record: any) => record.enableJump ? (v || <Tag color="orange">未设置</Tag>) : <Tag>不跳转</Tag>,
    },
    {
      title: '排序',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 80,
      align: 'center' as const,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (v: string) => v === 'enabled'
        ? <Tag color="green">启用</Tag>
        : <Tag color="default">禁用</Tag>,
    },
    { title: '创建时间', dataIndex: 'createTime', key: 'createTime', width: 180 },
    {
      title: '操作',
      key: 'action',
      width: 160,
      render: (_: any, record: any) => (
        <Space>
          <Button type="link" size="small" onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Popconfirm title="确定删除该轮播图？" onConfirm={() => handleRemove(record._id)}>
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Button type="primary" ghost icon={<PlusOutlined />} onClick={handleCreate}>
        新建轮播图
      </Button>
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={dataArray}
        loading={isValidating}
        pagination={false}
      />

      {/* 新增/编辑 抽屉 */}
      <Drawer
        title={isEdit ? '编辑轮播图' : '新建轮播图'}
        width={680}
        open={drawer.visible}
        onClose={() => {
          setDrawer({ visible: false, info: null })
          form.resetFields()
        }}
        destroyOnClose
        footer={
          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button style={{ width: 120 }} onClick={() => {
              setDrawer({ visible: false, info: null })
              form.resetFields()
            }}>取消</Button>
            <Button style={{ width: 120 }} type="primary" onClick={handleSubmit}>确认</Button>
          </Space>
        }
      >
        <Form {...layout} form={form}
          initialValues={isEdit ? drawer.info : { enableJump: false, sortOrder: 0, status: 'enabled' }}
        >
          <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入轮播图标题' }]}>
            <Input placeholder="如 周年庆活动" />
          </Form.Item>
          <Form.Item
            name="imageUrl"
            label="轮播图"
            rules={[{ required: true, message: '请上传轮播图' }]}
            extra={`推荐尺寸：${BANNER_SIZE.label}（${BANNER_SIZE.width}×${BANNER_SIZE.height}），其他尺寸将被拒绝`}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space>
                <Upload
                  name="image"
                  showUploadList={false}
                  headers={{
                    Authorization: `Bearer ${jsCookie.get('token_jwt') || ''}`
                  }}
                  action={`${config.host}/api/platform/activity/uploadImage`}
                  beforeUpload={validateImageSize}
                  onChange={handleUpload}
                >
                  <Button icon={<UploadOutlined />} loading={uploading}>上传</Button>
                </Upload>
              </Space>
              {imageUrl && (
                <Image
                  src={`${config.imgHost}${imageUrl.startsWith('/') ? imageUrl.slice(1) : imageUrl}`}
                  alt="预览"
                  style={{ maxWidth: 200, maxHeight: 100, objectFit: 'cover', borderRadius: 4, border: '1px solid #d9d9d9' }}
                  preview={{ mask: '预览' }}
                />
              )}
            </Space>
          </Form.Item>
          <Form.Item name="linkUrl" label="跳转链接">
            <Input placeholder="如 /activity/xxx 或 https://..." />
          </Form.Item>
          <Form.Item name="enableJump" label="是否跳转" valuePropName="checked">
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>
          <Form.Item name="sortOrder" label="排序权重">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="数字越小越靠前" />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select>
              <Select.Option value="enabled">启用</Select.Option>
              <Select.Option value="disabled">禁用</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Drawer>
    </Space>
  )
}

export default Banner
